import { useMemo, useState } from "react";
import AMCReportTable from "./AMCReportTable";
import { Col, Row } from "react-bootstrap";
import { getFormatter } from "views/merchandise/metrics";
import { useAppSelector } from "redux/hooks";
import { formatAdType } from "./utils";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { Button, ButtonGroup } from "@material-ui/core";
import PieChart from "components/core/blocks/PieChart";

const TOTAL_METRICS = ["total_product_sales", "total_cost", "impressions", "clicks", "conversions"];

const CHART_METRIC_OPTIONS = [
  {
    value: "total_product_sales",
    label: "Total Product Sales",
    format: "currency",
  },
  {
    value: "total_cost",
    label: "Total Cost",
    format: "currency",
  },
  {
    value: "campaign_cnt",
    label: "Campaign Count",
    format: "number",
  },
  {
    value: "clicks",
    label: "Clicks",
    format: "number",
  },
  {
    value: "impressions",
    label: "Impressions",
    format: "number",
  },
];

const Metric = ({ title, value, format }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const formatter = getFormatter(marketPlace, format);
  return (
    <Col key={title} className="fs-5 text-center align-items-flex-start">
      <span style={{ whiteSpace: "nowrap" }}>{title}</span>
      <span className="fs-3 fw-bold d-block">{formatter(value)}</span>
    </Col>
  );
};

const AMCFirstTouchAttrReport = ({ reportData, reportDetails, queryDetails }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0].value);
  const selectedMetric = useMemo(
    () => CHART_METRIC_OPTIONS.find((option) => option.value === pieChartMetric),
    [pieChartMetric],
  );
  const formatter = getFormatter(marketPlace, selectedMetric.format);

  const byAdType = useMemo(() => {
    const totalsByAdType = reportData.reduce((acc, row) => {
      const adType = row["ad_product_type"];

      if (!acc[adType]) {
        acc[adType] = {
          campaign_cnt: 0,
          ...TOTAL_METRICS.reduce((acc, metric) => {
            acc[metric] = 0;
            return acc;
          }, {}),
        };
      }
      acc[adType]["campaign_cnt"] += 1;
      TOTAL_METRICS.forEach((metric) => {
        acc[adType][metric] += row[metric];
      });
      return acc;
    }, {});
    Object.keys(totalsByAdType).forEach((adType) => {
      const total = totalsByAdType[adType];
      total["roas"] = total["total_product_sales"] / total["total_cost"];
      total["acos"] = total["total_cost"] / total["total_product_sales"];
      total["ctr"] = total["clicks"] / total["impressions"];
      total["conv_rate"] = total["conversions"] / total["clicks"];
    });
    return totalsByAdType;
  }, [reportData]);

  const pieChartData = useMemo(() => {
    return (
      Object.keys(byAdType)
        .map((adType) => {
          const total = byAdType[adType];
          return {
            name: formatAdType(adType),
            y: total[pieChartMetric],
          };
        })
        ?.filter((type) => type.y) ?? []
    );
  }, [byAdType, pieChartMetric]);

  return (
    <Row>
      <Col xs={12} xl={6}>
        <div className="py-5">
          <PieChart
            data={pieChartData}
            nameAccessor={(d) => d.name}
            accessor={(d) => d.y}
            formatter={formatter}
            height={460}
          />
        </div>
        <div className="d-flex justify-content-center align-items-center pt-5 mt-5">
          <ButtonGroup>
            {CHART_METRIC_OPTIONS?.map((group) => {
              const { value, label } = group;
              return (
                <Button
                  key={value}
                  onClick={() => setPieChartMetric(value)}
                  className="fs-6"
                  variant={selectedMetric.value === value ? "contained" : null}
                  color={selectedMetric.value === value ? "secondary" : null}
                >
                  {label}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
      </Col>
      <Col xs={12} xl={6}>
        {Object.keys(byAdType).map((adType) => {
          const total = byAdType[adType];
          return (
            <div
              key={adType}
              className="me-4 p-4 mb-3"
              style={{
                height: "150px",
                boxShadow: "none",
                border: "2px solid rgb(238, 238, 238)",
                borderRadius: "25px",
              }}
            >
              <Row className="ps-4">
                <Col xs={12} className="pb-1">
                  <span className="fs-4 fw-bold">{formatAdType(adType)}</span>
                </Col>
              </Row>
              <Row className="d-flex h-100 align-items-center">
                <Metric title="Campaign Count" value={total["campaign_cnt"]} format="number" />
                <Metric title="Total Product Sales" value={total["total_product_sales"]} format="currency" />
                <Metric title="Total Cost" value={total["total_cost"]} format="currency" />
                <Metric title="Impressions" value={total["impressions"]} format="number" />
                <Metric title="Clicks" value={total["clicks"]} format="number" />
                <Metric title="Conversions" value={total["conversions"]} format="number" />
                {acosOrRoas(
                  <Metric title="ACOS" value={total["acos"]} format="percent" />,
                  <Metric title="ROAS" value={total["roas"]} format="number" />,
                )}
                <Metric title="CTR" value={total["ctr"]} format="percent" />
                <Metric title="Conversion Rate" value={total["conv_rate"]} format="percent" />
              </Row>
            </div>
          );
        })}
      </Col>
      <Col xs={12}>
        <AMCReportTable reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />
      </Col>
    </Row>
  );
};

export default AMCFirstTouchAttrReport;
